import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, User, FileText, TrendingUp, FolderOpen, History,
  Download, CreditCard, Phone, Mail, MapPin, Calendar,
  Clock, Briefcase, Building2, DollarSign, Award,
  AlertCircle, CheckCircle, XCircle, Edit2, Users, Heart, Shield, Star,
  FileDown
} from 'lucide-react';
import api from '../../../api/config';
import { getEmployeePhotoUrl } from '../../../utils/imageUtils';
import ComprobantesModal from './ComprobantesModal';
import EmployeeAccessSection from './EmployeeAccessSection';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateString; }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return dateString; }
};

const ALL_TABS = [
  { id: 'personal',  label: 'Personal',   icon: User,       adminOnly: false },
  { id: 'related',   label: 'Familia',    icon: Users,      adminOnly: false },
  { id: 'contract',  label: 'Contrato',   icon: FileText,   adminOnly: false },
  { id: 'actions',   label: 'Acciones',   icon: TrendingUp, adminOnly: false },
  { id: 'documents', label: 'Documentos', icon: FolderOpen, adminOnly: false },
  { id: 'history',   label: 'Historial',  icon: History,    adminOnly: false },
  { id: 'access',    label: 'Acceso',     icon: Shield,     adminOnly: true  },
];

export default function EmployeeFileModal({ employee, onClose, onEdit, readOnly = false }) {
  const TABS = ALL_TABS.filter(t => !readOnly || !t.adminOnly);
  const [activeTab, setActiveTab]         = useState('personal');
  const [loading, setLoading]             = useState(false);
  const [contractData, setContractData]   = useState(null);
  const [personnelActions, setPersonnelActions] = useState([]);
  const [documents, setDocuments]         = useState([]);
  const [history, setHistory]             = useState([]);
  const [relatedPersons, setRelatedPersons] = useState([]);
  const [showComprobantes, setShowComprobantes] = useState(false);

  useEffect(() => {
    if (employee) loadEmployeeData();
  }, [employee]); // eslint-disable-line

  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const [contractRes, actionsRes, docsRes, historyRes, relatedRes] = await Promise.all([
        api.get(`/contracts?employeeId=${employee.id}`).catch(() => ({ data: [] })),
        api.get(`/personal-actions/employee/${employee.id}`).catch(() => ({ data: [] })),
        api.get(`/employee-documents/employee/${employee.id}`).catch(() => ({ data: [] })),
        api.get(`/employee-history/employee/${employee.id}?activo=all`).catch(() => ({ data: [] })),
        api.get(`/related-persons/employee/${employee.id}`).catch(() => ({ data: [] })),
      ]);

      const contracts = contractRes.data?.contracts || contractRes.data || [];
      setContractData(Array.isArray(contracts) ? contracts.find(c => c?.estado === 'activo') || null : null);

      const actions   = actionsRes.data?.actions   || actionsRes.data   || [];
      const docs      = docsRes.data?.documents    || docsRes.data      || [];
      const hist      = historyRes.data?.history   || historyRes.data   || [];
      const related   = Array.isArray(relatedRes.data) ? relatedRes.data : [];

      setPersonnelActions(Array.isArray(actions) ? actions : []);
      setDocuments(Array.isArray(docs) ? docs : []);
      setHistory(Array.isArray(hist) ? hist : []);
      setRelatedPersons(related);
    } catch (error) {
      console.error('Error loading employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNivelEducativo = (nivel) => {
    const map = {
      primaria: 'Primaria', secundaria: 'Secundaria',
      tecnico_superior: 'Técnico Superior',
      estudiante_universitario: 'Estudiante Universitario',
      grado_universitario: 'Grado Universitario',
      post_grado: 'Post Grado',
    };
    return map[nivel] || nivel || 'N/A';
  };


  const initials = `${employee.nombre?.[0] || ''}${employee.apellido?.[0] || ''}`.toUpperCase();

  return (
    <div className="space-y-0">

      {/* ── Page Header ─────────────────────────────────────── */}
      {!readOnly && (
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
            Volver a Empleados
          </button>
        </div>
      )}

      {/* ── Employee card header ─────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {employee.foto ? (
              <img
                src={getEmployeePhotoUrl(employee.foto)}
                alt={employee.nombre}
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div
              className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
              style={{ display: employee.foto ? 'none' : 'flex' }}
            >
              {initials}
            </div>

            {/* Info */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {employee.nombre} {employee.apellido}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                <span className="font-mono text-blue-600">{employee.employee_id || employee.employeeId}</span>
                {(employee.cargo || employee.cargo_nombre) && (
                  <> · {employee.cargo || employee.cargo_nombre}</>
                )}
                {(employee.departamento || employee.departamento_nombre) && (
                  <> · {employee.departamento || employee.departamento_nombre}</>
                )}
              </p>
              <span className={`inline-flex items-center gap-1 mt-2 text-xs font-medium px-2.5 py-0.5 rounded-full ${
                employee.estado === 'activo'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-600'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${employee.estado === 'activo' ? 'bg-green-500' : 'bg-red-400'}`} />
                {employee.estado === 'activo' ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {!readOnly && (
              <button
                onClick={() => window.print()}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Imprimir expediente"
              >
                <Download size={16} />
              </button>
            )}
            <button
              onClick={() => setShowComprobantes(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Ver comprobantes de pago"
            >
              <FileDown size={14} />
              Comprobantes
            </button>
            {!readOnly && onEdit && (
              <button
                onClick={() => onEdit(employee)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit2 size={14} />
                Editar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal de Comprobantes ───────────────────────────── */}
      {showComprobantes && (
        <ComprobantesModal employee={employee} onClose={() => setShowComprobantes(false)} />
      )}

      {/* ── Tabs + Content ───────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Tab bar */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Cargando expediente...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'personal'  && <PersonalInfoTab employee={employee} formatNivelEducativo={formatNivelEducativo} />}
              {activeTab === 'related'   && <RelatedPersonsTab persons={relatedPersons} />}
              {activeTab === 'contract'  && <ContractTab contract={contractData} />}
              {activeTab === 'actions'   && <ActionsTab actions={personnelActions} />}
              {activeTab === 'documents' && <DocumentsTab documents={documents} employeeId={employee.id} onRefresh={loadEmployeeData} />}
              {activeTab === 'history'   && <HistoryTab history={history} />}
              {activeTab === 'access'    && <EmployeeAccessSection employee={employee} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// TAB COMPONENTS
// ═══════════════════════════════════════════════════

function InfoField({ icon, label, value, className = '' }) {
  return (
    <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="text-gray-400">{React.cloneElement(icon, { className: 'w-4 h-4' })}</div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-sm font-medium text-gray-800 ml-6">{value || 'No especificado'}</p>
    </div>
  );
}

function PersonalInfoTab({ employee, formatNivelEducativo }) {
  return (
    <div className="space-y-6">
      <Section title="Información Personal" icon={<User size={16} className="text-blue-600" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <InfoField icon={<User />}     label="Cédula"              value={employee.cedula} />
          <InfoField icon={<Calendar />} label="Fecha Nacimiento"    value={employee.fechaNacimiento || employee.fecha_nacimiento} />
          <InfoField icon={<Clock />}    label="Edad"                value={employee.edad ? `${employee.edad} años` : null} />
          <InfoField icon={<User />}     label="Sexo"                value={employee.sexo} />
          <InfoField icon={<MapPin />}   label="Nacionalidad"        value={employee.nacionalidad} />
          <InfoField icon={<MapPin />}   label="País"                value={employee.pais} />
          <InfoField icon={<User />}     label="Tipo de Sangre"      value={employee.tipoSangre || employee.tipo_sangre} />
          <InfoField icon={<Briefcase />}label="Profesión"           value={employee.profesion} />
          <InfoField icon={<User />}     label="Nivel Educativo"     value={formatNivelEducativo(employee.nivelEducativo || employee.nivel_educativo)} />
          <InfoField icon={<Phone />}    label="Teléfono"            value={employee.telefono} />
          <InfoField icon={<Phone />}    label="Celular"             value={employee.celular} />
          <InfoField icon={<Mail />}     label="Email"               value={employee.email} />
          <InfoField icon={<MapPin />}   label="Dirección"           value={employee.direccion} className="md:col-span-3" />
          <InfoField icon={<User />}     label="NSS"                 value={employee.nss} />
          <InfoField icon={<Phone />}    label="Contacto Emergencia" value={employee.contactoEmergencia || employee.contacto_emergencia} />
        </div>
      </Section>

      <Section title="Información Bancaria" icon={<CreditCard size={16} className="text-green-600" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InfoField icon={<CreditCard />} label="Banco"          value={employee.nombreBanco || employee.nombre_banco} />
          <InfoField icon={<CreditCard />} label="Número de Cuenta" value={employee.cuentaBanco || employee.cuenta_banco} />
        </div>
      </Section>
    </div>
  );
}

function ContractTab({ contract }) {
  if (!contract) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-10 text-center">
        <AlertCircle size={36} className="text-amber-500 mx-auto mb-3" />
        <h3 className="text-base font-bold text-amber-900 mb-1">Sin Contrato Activo</h3>
        <p className="text-sm text-amber-700">Crear uno desde el módulo de Contratos.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle size={16} className="text-green-600" />
        <span className="text-sm font-semibold text-green-800">Contrato activo</span>
        {contract.numero_contrato && (
          <span className="ml-auto text-xs text-green-700 font-mono">{contract.numero_contrato}</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoField icon={<Building2 />}   label="Departamento"    value={contract.departamento || contract.departamento_nombre} />
        <InfoField icon={<Briefcase />}   label="Cargo"           value={contract.cargo || contract.cargo_nombre} />
        <InfoField icon={<Calendar />}    label="Fecha Inicio"    value={formatDate(contract.fecha_inicio)} />
        <InfoField icon={<Calendar />}    label="Fecha Fin"       value={contract.fecha_fin ? formatDate(contract.fecha_fin) : 'Indefinido'} />
        <InfoField icon={<DollarSign />}  label="Salario Base"    value={`RD$ ${parseFloat(contract.salario_base || contract.salario || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`} />
        <InfoField icon={<FileText />}    label="Tipo Contrato"   value={contract.tipo_contrato} />
        <InfoField icon={<Clock />}       label="Tipo Nómina"     value={contract.tipo_nomina} />
        <InfoField icon={<Clock />}       label="Turno"           value={contract.turno} />
      </div>
      {contract.documento_url && (
        <a
          href={contract.documento_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download size={14} />
          Descargar contrato
        </a>
      )}
    </div>
  );
}

function ActionsTab({ actions }) {
  if (actions.length === 0) {
    return <EmptyState icon={<TrendingUp size={32} />} title="Sin acciones registradas" />;
  }

  const getActionCls = (tipo) => {
    const map = {
      promocion: 'border-green-200 bg-green-50',
      cambio_puesto: 'border-blue-200 bg-blue-50',
      sancion: 'border-red-200 bg-red-50',
      reconocimiento: 'border-amber-200 bg-amber-50',
    };
    return map[tipo] || 'border-gray-200 bg-gray-50';
  };

  return (
    <div className="space-y-3">
      {actions.map((action) => (
        <div key={action.id} className={`border rounded-xl p-4 ${getActionCls(action.tipo_accion)}`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                {action.titulo || action.tipo_accion?.replace('_', ' ').toUpperCase()}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {formatDate(action.fecha_efectiva || action.fecha_accion)}
              </p>
            </div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              action.estado === 'aprobado' ? 'bg-green-100 text-green-700' :
              action.estado === 'pendiente' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {action.estado}
            </span>
          </div>
          {action.descripcion && (
            <p className="text-sm text-gray-600">{action.descripcion}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ documents, onRefresh }) {
  if (!documents || documents.length === 0) {
    return <EmptyState icon={<FolderOpen size={32} />} title="Sin documentos cargados" />;
  }

  const downloadDocument = async (docId, filename) => {
    try {
      const response = await api.get(`/employee-documents/${docId}/download`, { responseType: 'blob' });
      // response.data ya es un Blob con el Content-Type correcto — no envolver
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error al descargar: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="space-y-3">
      {documents.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {doc.nombre || doc.documento_nombre || doc.nombre_documento || doc.archivo_nombre || '—'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {doc.tipo_nombre || doc.tipo_documento_codigo || '—'} · {formatDateTime(doc.created_at)}
              </p>
            </div>
          </div>
          <button
            onClick={() => downloadDocument(doc.id, doc.archivo_nombre || doc.nombre + '.pdf')}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Descargar"
          >
            <Download size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}

function parseCambio(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
}

function CambioSalarial({ anterior, nuevo }) {
  const fmt = (v) => parseFloat(v).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (anterior?.salario == null && nuevo?.salario == null) return null;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
      {anterior?.salario != null && (
        <span className="bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-500 line-through">
          RD$ {fmt(anterior.salario)}
        </span>
      )}
      {anterior?.salario != null && nuevo?.salario != null && (
        <span className="text-gray-400">→</span>
      )}
      {nuevo?.salario != null && (
        <span className="bg-white border border-green-300 rounded px-2 py-0.5 font-semibold text-green-700">
          RD$ {fmt(nuevo.salario)}
        </span>
      )}
    </div>
  );
}

function CambioTexto({ label, anterior, nuevo }) {
  if (!anterior && !nuevo) return null;
  return (
    <div className="mt-1 text-xs text-gray-500">
      <span className="font-medium">{label}: </span>
      {anterior && <span className="line-through mr-1">{anterior}</span>}
      {anterior && nuevo && <span className="mr-1">→</span>}
      {nuevo && <span className="font-semibold text-gray-700">{nuevo}</span>}
    </div>
  );
}

function HistoryTab({ history }) {
  if (history.length === 0) {
    return <EmptyState icon={<History size={32} />} title="Sin historial registrado" />;
  }

  const getCls = (cat) => {
    const map = {
      contractual: 'border-blue-200 bg-blue-50',
      salarial: 'border-green-200 bg-green-50',
      organizacional: 'border-gray-200 bg-gray-50',
      disciplinaria: 'border-red-200 bg-red-50',
      reconocimiento: 'border-amber-200 bg-amber-50',
    };
    return map[cat] || 'border-gray-200 bg-gray-50';
  };

  return (
    <div className="space-y-3">
      {history.map((entry) => {
        const anterior = parseCambio(entry.valor_anterior);
        const nuevo    = parseCambio(entry.valor_nuevo);
        return (
          <div key={entry.id} className={`border rounded-xl p-4 ${getCls(entry.categoria)}`}>
            <div className="flex items-start justify-between mb-1">
              <p className="font-semibold text-gray-900 text-sm">{entry.titulo}</p>
              <span className="text-xs text-gray-400">{formatDate(entry.fecha_evento || entry.fecha_efectiva)}</span>
            </div>
            {entry.descripcion && (
              <p className="text-sm text-gray-600 mt-1">{entry.descripcion}</p>
            )}
            {/* Cambios salariales */}
            <CambioSalarial anterior={anterior} nuevo={nuevo} />
            {/* Cambios de cargo */}
            <CambioTexto label="Cargo" anterior={anterior?.cargo} nuevo={nuevo?.cargo} />
            {/* Cambios de departamento */}
            <CambioTexto label="Departamento" anterior={anterior?.departamento} nuevo={nuevo?.departamento} />
            {entry.created_at && (
              <p className="text-xs text-gray-400 mt-2">
                Registrado: {formatDateTime(entry.created_at)}
                {entry.created_by && ` · por ${entry.created_by}`}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

const RELATIONSHIP_LABELS = {
  conyuge: 'Cónyuge', hijo: 'Hijo', hija: 'Hija',
  padre: 'Padre', madre: 'Madre',
  hermano: 'Hermano', hermana: 'Hermana',
  abuelo: 'Abuelo', abuela: 'Abuela',
  nieto: 'Nieto', nieta: 'Nieta', otro: 'Otro',
};

function RelatedPersonsTab({ persons }) {
  if (!persons || persons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="text-gray-300 mb-3"><Users size={32} /></div>
        <p className="text-sm font-medium text-gray-400">No hay personas relacionadas registradas</p>
      </div>
    );
  }

  const active   = persons.filter(p => p.active !== false);
  const inactive = persons.filter(p => p.active === false);

  const Badge = ({ show, icon, label, color }) => show ? (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>
      {icon}{label}
    </span>
  ) : null;

  const PersonCard = ({ p }) => (
    <div className={`border rounded-xl p-4 ${p.active === false ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200 bg-white hover:border-gray-300'} transition-colors`}>
      <div className="flex items-start justify-between gap-3">
        {/* Avatar + nombre */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-blue-600">
              {p.first_name?.[0]?.toUpperCase()}{p.last_name?.[0]?.toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">
              {p.first_name} {p.last_name}
            </p>
            <p className="text-xs text-gray-500">
              {RELATIONSHIP_LABELS[p.relationship] || p.relationship}
              {p.gender && ` · ${p.gender === 'M' ? 'Masculino' : p.gender === 'F' ? 'Femenino' : p.gender}`}
            </p>
          </div>
        </div>

        {/* Badges de rol */}
        <div className="flex flex-wrap gap-1.5 justify-end shrink-0">
          <Badge show={p.is_health_dependent}    icon={<Heart   size={10} />} label="ARS"       color="bg-blue-100 text-blue-700"  />
          <Badge show={p.is_additional_dependent} icon={<Shield  size={10} />} label="Dep. Adic." color="bg-indigo-100 text-indigo-700" />
          <Badge show={p.is_emergency_contact}   icon={<Phone   size={10} />} label="Emergencia" color="bg-amber-100 text-amber-700" />
          <Badge show={p.is_beneficiary}         icon={<Star    size={10} />} label="Beneficiario" color="bg-green-100 text-green-700" />
        </div>
      </div>

      {/* Detalles secundarios */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {p.birth_date && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={12} className="text-gray-400" />
            {formatDate(p.birth_date)}
          </div>
        )}
        {p.document_number && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <User size={12} className="text-gray-400" />
            {p.document_number}
          </div>
        )}
        {p.phone && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Phone size={12} className="text-gray-400" />
            {p.phone}
          </div>
        )}
        {p.notes && (
          <div className="col-span-2 flex items-start gap-1.5 text-xs text-gray-500">
            <FileText size={12} className="text-gray-400 mt-0.5 shrink-0" />
            {p.notes}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Resumen de roles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Dependientes ARS',      count: persons.filter(p => p.is_health_dependent && p.active !== false).length,    color: 'text-blue-600',   bg: 'bg-blue-50'   },
          { label: 'Dep. Adicionales',       count: persons.filter(p => p.is_additional_dependent && p.active !== false).length, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Contactos Emergencia',   count: persons.filter(p => p.is_emergency_contact && p.active !== false).length,   color: 'text-amber-600',  bg: 'bg-amber-50'  },
          { label: 'Beneficiarios',          count: persons.filter(p => p.is_beneficiary && p.active !== false).length,         color: 'text-green-600',  bg: 'bg-green-50'  },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-tight">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Lista activos */}
      {active.length > 0 && (
        <Section title={`Personas activas (${active.length})`} icon={<Users size={16} className="text-blue-600" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {active.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </Section>
      )}

      {/* Lista inactivos */}
      {inactive.length > 0 && (
        <Section title={`Inactivos (${inactive.length})`} icon={<Users size={16} className="text-gray-400" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {inactive.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function EmptyState({ icon, title }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <div className="text-gray-300 mb-3">{icon}</div>
      <p className="text-sm font-medium text-gray-400">{title}</p>
    </div>
  );
}
